import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { registerRefusalTranslator } from './api-error';
import type { ApiClientError, FailedRule } from './api-error';

/** One field's refusal, ready to render. */
export interface FieldRefusal {
  /** The wire name, so a form can find the control it belongs to. */
  readonly field: string;
  /** What the field is called in the reader's language. */
  readonly label: string;
  /** Why it was refused, in the reader's language. */
  readonly reason: string;
}

/**
 * Wire names that are not fields.
 *
 * Laravel keys an array element's error by its path, so a refused invoice line
 * arrives as `items.0.quantity`. Rendering that verbatim asks a clerk to count
 * from zero. The index is lifted out and said as a position instead.
 */
const INDEXED = /^(.*?)\.(\d+)(?:\.(.*))?$/;

/**
 * Turning a refusal into a sentence the reader can act on.
 *
 * The server sends the rule that failed and what it was measured against --
 * `before_or_equal` with `now`, `min` with `5` -- beside the English sentence
 * it has always sent. This builds the sentence here instead, for two reasons.
 *
 * **It follows the reader.** The app switches language without reloading. A
 * sentence translated on the server arrives fixed in whichever language was
 * current when the request went out, and would still be in it after the reader
 * switches. Building it here means the refusal on screen changes with
 * everything else.
 *
 * **It names the field the way the screen does.** "The discharge datetime
 * field" is the column name; the label beside the input says "Discharge time".
 * A refusal that uses a different word for the same box makes the reader hunt.
 *
 * The server's own sentence is kept as the fallback and used whenever a rule
 * has no entry here, so a rule nobody has translated yet still says something
 * true rather than nothing.
 */
@Injectable({ providedIn: 'root' })
export class ValidationMessages {
  private readonly transloco = inject(TranslocoService);

  constructor() {
    /*
     * Installed once, at start-up, so that `normalizeApiError` -- a free
     * function seventy-four screens already call for the message they show --
     * returns that message in the reader's language.
     *
     * The alternative was rewriting seventy-four catch blocks to hold the
     * error and render it. This is one line, and it means a screen nobody has
     * touched still tells the truth in Arabic.
     */
    registerRefusalTranslator((fieldErrors, failedRules) => {
      const fields = Object.keys(fieldErrors);
      if (fields.length === 0) {
        return null;
      }

      const first = fields[0]!;
      const reason = this.reason(first, failedRules[first] ?? [], fieldErrors[first]?.[0] ?? '');
      const headline = `${this.label(first)}: ${reason}`;

      return fields.length === 1
        ? headline
        : `${headline} ${this.transloco.translate('validation.andOthers', { count: fields.length - 1 })}`;
    });
  }

  /** Every field that was refused, in the order the server listed them. */
  refusals(error: ApiClientError): readonly FieldRefusal[] {
    return Object.entries(error.fieldErrors).map(([field, messages]) => ({
      field,
      label: this.label(field),
      reason: this.reason(field, error.failedRules[field] ?? [], messages[0] ?? ''),
    }));
  }

  /** One field's reason, for a form that shows it under the input itself. */
  reasonFor(error: ApiClientError, field: string): string | null {
    const messages = error.fieldErrors[field];
    if (!messages || messages.length === 0) {
      return null;
    }

    return this.reason(field, error.failedRules[field] ?? [], messages[0] ?? '');
  }

  /**
   * What the field is called on screen.
   *
   * `fields.<name>` in the catalogue, falling back to the wire name with its
   * separators opened out. The fallback is deliberately readable rather than
   * blank: a field nobody has named yet should read as `service price` and not
   * as `fields.service_price`.
   */
  label(field: string): string {
    const indexed = INDEXED.exec(field);
    if (indexed) {
      const [, prefix, index, rest] = indexed;
      const inner = rest ? this.label(rest) : this.label(prefix ?? '');

      return this.transloco.translate('validation.atPosition', {
        field: inner,
        position: Number(index) + 1,
      });
    }

    const key = `fields.${field}`;
    const translated = this.transloco.translate(key);

    return translated === key ? field.replace(/[._]/g, ' ') : translated;
  }

  private reason(field: string, rules: readonly FailedRule[], fallback: string): string {
    for (const rule of rules) {
      const key = `validation.rule.${rule.rule}`;
      const translated = this.transloco.translate(key, {
        field: this.label(field),
        first: rule.parameters[0] ?? '',
        second: rule.parameters[1] ?? '',
        // Joined here rather than in the catalogue: a list separator is a
        // language's own, and the catalogue string cannot choose one.
        list: rule.parameters.join(this.transloco.translate('validation.listSeparator')),
      });

      if (translated !== key) {
        return translated;
      }
    }

    // The server's English sentence. Not nothing, and not a lie.
    return fallback;
  }
}
